/**
 * Compress an image file client-side using Canvas.
 * Resizes to max 1200px width/height, compresses to JPEG ~80% quality,
 * and adds a diagonal "Karanki Matrimony" watermark.
 */
export function compressImage(file, { maxWidth = 1200, maxHeight = 1200, quality = 0.8 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) {
      reject(new Error("Not an image file"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        // Calculate new dimensions maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Draw to canvas
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Add diagonal watermark
        addWatermark(ctx, width, height);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Compression failed"));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = () => reject(new Error("Could not load image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * Adds diagonal repeating "Karanki Matrimony" watermark.
 * Small font, covers the image evenly based on dimensions, subtle but visible.
 */
function addWatermark(ctx, width, height) {
  const text = "Karanki Matrimony";
  // Small font — 2% of the shorter side, minimum 10px
  const fontSize = Math.max(10, Math.round(Math.min(width, height) * 0.02));

  ctx.save();

  // Rotate -35 degrees for diagonal
  ctx.translate(width / 2, height / 2);
  ctx.rotate(-35 * Math.PI / 180);
  ctx.translate(-width / 2, -height / 2);

  ctx.font = `500 ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Spacing based on image dimensions — evenly distributed
  const spacingX = fontSize * 14;
  const spacingY = fontSize * 6;
  const diagonal = Math.sqrt(width * width + height * height);
  const startX = -diagonal * 0.3;
  const startY = -diagonal * 0.3;
  const endX = diagonal * 1.3;
  const endY = diagonal * 1.3;

  // White text with subtle opacity
  ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
  let row = 0;
  for (let y = startY; y < endY; y += spacingY) {
    const offsetX = (row % 2) * (spacingX / 2); // Stagger alternate rows
    for (let x = startX + offsetX; x < endX; x += spacingX) {
      ctx.fillText(text, x, y);
    }
    row++;
  }

  ctx.restore();
}

/**
 * Compress multiple image files.
 */
export async function compressImages(files, options) {
  const results = [];
  for (const file of files) {
    try {
      const compressed = await compressImage(file, options);
      results.push(compressed);
    } catch {
      results.push(file);
    }
  }
  return results;
}
